import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";

function parseAmount(value) {
  if (value == null || value === "") return 0;
  const amount = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
}

function scopeKey(value) {
  return value == null || value === "" ? "none" : String(value);
}

function samePlace(left, right) {
  return scopeKey(left) === scopeKey(right);
}

function locationGroupKey(
  ingredientId,
  search,
  productTypeId,
  locationId,
  zoneId,
) {
  return `${ingredientId}|${search || ""}|${productTypeId || ""}|${locationId || ""}|${zoneId || ""}`;
}

function zoneGroupKey(ingredientId, locationId, search, productTypeId, zoneId) {
  return `${ingredientId}|${scopeKey(locationId)}|${search || ""}|${productTypeId || ""}|${zoneId || ""}`;
}

function lotGroupKey(ingredientId, locationId, zoneId, search, productTypeId) {
  return `${ingredientId}|${scopeKey(locationId)}|${scopeKey(zoneId)}|${search || ""}|${productTypeId || ""}`;
}

function cartonKey(
  ingredientId,
  locationId,
  zoneId,
  lotNumber,
  search,
  productTypeId,
) {
  return `${ingredientId}|${scopeKey(locationId)}|${scopeKey(zoneId)}|${scopeKey(lotNumber)}|${search || ""}|${productTypeId || ""}`;
}

function applyGroupDelta(rows, matches, stockDelta, oldItemCost, newItemCost) {
  if (!rows) {
    return rows;
  }

  return rows.flatMap((row) => {
    if (!matches(row)) {
      return [row];
    }

    const nextStock = parseAmount(row.total_stock) - stockDelta;
    const nextCost =
      parseAmount(row.total_cost) -
      parseAmount(oldItemCost) +
      parseAmount(newItemCost);
    if (nextStock <= 0) {
      return [];
    }

    return [
      {
        ...row,
        total_stock: nextStock.toFixed(2),
        total_cost: nextCost.toFixed(2),
      },
    ];
  });
}

export function InventoryRawMaterialViewModel(logout) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({ data: [] });
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [locationDetails, setLocationDetails] = useState({});
  const [zoneDetails, setZoneDetails] = useState({});
  const [lotDetails, setLotDetails] = useState({});
  const [cartonDetails, setCartonDetails] = useState({});
  const [loadingLocations, setLoadingLocations] = useState({});
  const [loadingZones, setLoadingZones] = useState({});
  const [loadingLots, setLoadingLots] = useState({});
  const [loadingCartons, setLoadingCartons] = useState({});

  const clearDrilldown = () => {
    setLocationDetails({});
    setZoneDetails({});
    setLotDetails({});
    setCartonDetails({});
  };

  const getInStockRawMaterial = async function (
    search,
    page,
    product_type,
    locationId,
    zoneId,
  ) {
    setLoading(true);
    clearDrilldown();
    const payload = {
      page: page,
      search: search,
      product_type: product_type,
      location_id: locationId || null,
      zone_id: zoneId || null,
    };
    let callResult = await makePostApiCall("getInStockRawMaterial", payload);

    if (callResult.success) {
      setProducts(callResult.data);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
    setLoading(false);
  };

  const fetchLocations = async function (
    rowData,
    search,
    productTypeId,
    locationId,
    zoneId,
  ) {
    const key = locationGroupKey(
      rowData.id,
      search,
      productTypeId,
      locationId,
      zoneId,
    );
    const payload = {
      ingredient_id: rowData.id,
      search: search,
      product_type: productTypeId || null,
      location_id: locationId || null,
      zone_id: zoneId || null,
    };

    setLoadingLocations((prev) => ({ ...prev, [key]: true }));
    let callResult = await makePostApiCall(
      "inventoryRawMaterialLocations",
      payload,
    );
    setLoadingLocations((prev) => ({ ...prev, [key]: false }));

    if (callResult.success) {
      setLocationDetails((prev) => ({ ...prev, [key]: callResult.data }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchZones = async function (group, search, productTypeId, zoneId) {
    const key = zoneGroupKey(
      group.ingredient_id,
      group.location_id,
      search,
      productTypeId,
      zoneId,
    );
    const payload = {
      ingredient_id: group.ingredient_id,
      location_id: group.location_id ?? null,
      search: search,
      product_type: productTypeId || null,
      zone_id: zoneId || null,
    };

    setLoadingZones((prev) => ({ ...prev, [key]: true }));
    let callResult = await makePostApiCall(
      "inventoryRawMaterialZones",
      payload,
    );
    setLoadingZones((prev) => ({ ...prev, [key]: false }));

    if (callResult.success) {
      setZoneDetails((prev) => ({ ...prev, [key]: callResult.data }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchLots = async function (group, search, productTypeId) {
    const key = lotGroupKey(
      group.ingredient_id,
      group.location_id,
      group.zone_id,
      search,
      productTypeId,
    );
    const payload = {
      ingredient_id: group.ingredient_id,
      location_id: group.location_id ?? null,
      zone_id: group.zone_id ?? null,
      search: search,
      product_type: productTypeId || null,
    };

    setLoadingLots((prev) => ({ ...prev, [key]: true }));
    let callResult = await makePostApiCall("inventoryRawMaterialLots", payload);
    setLoadingLots((prev) => ({ ...prev, [key]: false }));

    if (callResult.success) {
      setLotDetails((prev) => ({ ...prev, [key]: callResult.data }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const fetchCartons = async function (group, search, productTypeId) {
    const key = cartonKey(
      group.ingredient_id,
      group.location_id,
      group.zone_id,
      group.lot_number,
      search,
      productTypeId,
    );
    const payload = {
      ingredient_id: group.ingredient_id,
      location_id: group.location_id ?? null,
      zone_id: group.zone_id ?? null,
      lot_number: group.lot_number ?? null,
      search: search,
      product_type: productTypeId || null,
    };

    setLoadingCartons((prev) => ({ ...prev, [key]: true }));
    let callResult = await makePostApiCall(
      "inventoryRawMaterialCartons",
      payload,
    );
    setLoadingCartons((prev) => ({ ...prev, [key]: false }));

    if (callResult.success) {
      setCartonDetails((prev) => ({ ...prev, [key]: callResult.data }));
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getInStockRawMaterialByBomPlu = async function (id) {
    let callResult = await makePostApiCall(
      `getInStockRawMaterialByBomPlu/${id}`,
    );

    if (callResult.success) {
      return callResult;
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const onUpdateReceivingItemStock = async function (
    id,
    newInventory,
    oldInventory,
    oldItemCost,
    newItemCost,
    reason,
    context,
  ) {
    let callResult = await makePostApiCall(`updateReceivingItemStock/${id}`, {
      newInventory: newInventory,
      newItemCost: newItemCost,
      reason: reason,
    });

    if (callResult.success) {
      const stockDelta = parseAmount(oldInventory) - parseAmount(newInventory);
      const formattedStock = parseAmount(newInventory).toFixed(2);
      const formattedItemCost = parseAmount(newItemCost).toFixed(2);
      const costChange = parseAmount(newItemCost) - parseAmount(oldItemCost);
      const ingredientId = context?.ingredientId;
      const locationId = context?.locationId;
      const zoneId = context?.zoneId;
      const lotNumber = context?.lotNumber;

      setCartonDetails((prev) => {
        const key = cartonKey(
          ingredientId,
          locationId,
          zoneId,
          lotNumber,
          context?.search,
          context?.productTypeId,
        );
        const rows = prev[key] ?? [];
        const nextRows =
          parseAmount(newInventory) <= 0
            ? rows.filter((row) => String(row.id) !== String(id))
            : rows.map((row) =>
                String(row.id) === String(id)
                  ? {
                      ...row,
                      in_stock: formattedStock,
                      old_in_stock: formattedStock,
                      total_cost: formattedItemCost,
                    }
                  : row,
              );

        return { ...prev, [key]: nextRows };
      });

      const adjust = (setter, key, matches) => {
        setter((prev) => ({
          ...prev,
          [key]: applyGroupDelta(
            prev[key],
            matches,
            stockDelta,
            oldItemCost,
            newItemCost,
          ),
        }));
      };

      adjust(
        setLotDetails,
        lotGroupKey(
          ingredientId,
          locationId,
          zoneId,
          context?.search,
          context?.productTypeId,
        ),
        (row) => samePlace(row.lot_number, lotNumber),
      );
      adjust(
        setZoneDetails,
        zoneGroupKey(
          ingredientId,
          locationId,
          context?.search,
          context?.productTypeId,
          context?.zoneFilter,
        ),
        (row) => samePlace(row.zone_id, zoneId),
      );
      adjust(
        setLocationDetails,
        locationGroupKey(
          ingredientId,
          context?.search,
          context?.productTypeId,
          context?.locationFilter,
          context?.zoneFilter,
        ),
        (row) => samePlace(row.location_id, locationId),
      );

      setProducts((prev) => {
        const data = (prev.data ?? []).map((row) => {
          if (String(row.id) !== String(ingredientId)) {
            return row;
          }

          const nextStock = parseAmount(row.total_stock) - stockDelta;
          const nextCost = parseAmount(row.total_cost) + costChange;

          return {
            ...row,
            total_stock: nextStock.toFixed(2),
            total_cost: nextCost.toFixed(2),
          };
        });

        return {
          ...prev,
          data,
          totalPageStockDollarAmount: (
            parseAmount(prev.totalPageStockDollarAmount) + costChange
          ).toFixed(2),
          totalStockDollarAmount: (
            parseAmount(prev.totalStockDollarAmount) + costChange
          ).toFixed(2),
        };
      });

      return true;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    return false;
  };

  const getProductTypes = async function () {
    let callResult = await makePostApiCall(`productTypesForDropdown`);

    if (callResult.success) {
      setProductTypeOptions(callResult.data);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getPromoteReceivingDefaults = async function (receivingAssemblyItemId) {
    return makeGetApiCall(
      `promoteReceivingDefaults/${receivingAssemblyItemId}`,
    );
  };

  const promoteReceivingToPackaging = async function (payload) {
    return makePostApiCall("promoteReceivingToPackaging", payload);
  };

  const exportNetSuiteRawInventoryCsv = async function () {
    let callResult = await makePostApiCall(`exportNetSuiteRawInventoryCsv`);
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  return {
    getInStockRawMaterial,
    getInStockRawMaterialByBomPlu,
    fetchLocations,
    fetchZones,
    fetchLots,
    fetchCartons,
    loading,
    loadingLocations,
    loadingZones,
    loadingLots,
    loadingCartons,
    products,
    locationDetails,
    zoneDetails,
    lotDetails,
    cartonDetails,
    locationGroupKey,
    zoneGroupKey,
    lotGroupKey,
    cartonKey,
    onUpdateReceivingItemStock,
    getProductTypes,
    productTypeOptions,
    exportNetSuiteRawInventoryCsv,
    getPromoteReceivingDefaults,
    promoteReceivingToPackaging,
  };
}
